import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    Alignment,
    Autoformat,
    BlockQuote,
    Bold,
    ClassicEditor,
    Emoji,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    Heading,
    Indent,
    Italic,
    Link,
    List,
    ListProperties,
    Mention,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    Strikethrough,
    Table,
    TableCellProperties,
    TableProperties,
    TableToolbar,
    Underline
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import axiosInstance from "../../../axiosConfig";
import { ChevronDown } from "lucide-react";
import "../css/destination.css";

class OverviewEditor extends ClassicEditor {}

OverviewEditor.builtinPlugins = [
    Essentials,
    Paragraph,
    Heading,
    Autoformat,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Link,
    List,
    ListProperties,
    BlockQuote,
    Alignment,
    Indent,
    FontFamily,
    FontSize,
    FontColor,
    FontBackgroundColor,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    PasteFromOffice,
    Mention,
    Emoji,
    RemoveFormat
];

OverviewEditor.defaultConfig = {
    licenseKey: "GPL",
    toolbar: {
        items: [
            "heading",
            "|",
            "fontFamily",
            "fontSize",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "removeFormat",
            "|",
            "alignment",
            "|",
            "bulletedList",
            "numberedList",
            "outdent",
            "indent",
            "|",
            "link",
            "emoji",
            "blockQuote",
            "insertTable",
            "|",
            "undo",
            "redo"
        ],
        shouldNotGroupWhenFull: true
    },
    heading: {
        options: [
            { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
            { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
            { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
            { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" }
        ]
    },
    fontFamily: {
        supportAllValues: true
    },
    fontSize: {
        options: [10, 12, 14, "default", 18, 20, 22, 24, 28, 32],
        supportAllValues: true
    },
    fontColor: {
        columns: 8,
        documentColors: 10
    },
    fontBackgroundColor: {
        columns: 8,
        documentColors: 10
    },
    link: {
        addTargetToExternalLinks: true,
        defaultProtocol: "https://"
    },
    list: {
        properties: {
            styles: true,
            startIndex: true,
            reversed: true
        }
    },
    table: {
        contentToolbar: [
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableProperties",
            "tableCellProperties"
        ]
    }
};

export default function DestinationBasicInfo({ destinationUuid, setDestinationUuid, setActiveTab, isViewMode, isEditMode }) {
    // Remove the local activeTab state since we'll use the parent's
    const [loading, setLoading] = useState(true);
    const [categorySearch, setCategorySearch] = useState('');
    const [subCategorySearch, setSubCategorySearch] = useState('');
    const [selectedCategoryCodes, setSelectedCategoryCodes] = useState([]);
    const [isReadOnly, setIsReadOnly] = useState(false);

    const [metaData, setMetaData] = useState({
        destinationTypes: [],
        ownershipTypes: [],
        categories: [],
        subCategories: [],
        cities: [],
        districts: [],
        reviewTags: [],
        destinationTags: [],
        contactTypes: [],
        emailTypes: [],
        mediaTypes: []
    });

    const [entities, setEntities] = useState([]);
    const [branches, setBranches] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        displayName: "",
        destinationTypeCode: "",
        ownershipTypeCode: "",
        shortDescription: "",
        description: "",
        subCategoryCodes: [],
        overview: "",
        highlight: "",
        tagCodes: [],
        reviewTagIds: [],
        ownershipEntityIds: [],
        ownershipEntityBranchIds: []
    });

    const scrollToTop = () => {
        const mainContent = document.querySelector('main');
        if (mainContent) {
            mainContent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const fetchMetaData = async () => {
            try {
                const response = await axiosInstance.get(
                    '/internal-api/v1/client-web/destination-panel/registration/meta'
                );
                if (response.data && response.data.result) {
                    setMetaData(response.data.result);
                }
            } catch (error) {
                console.error("Error fetching metadata:", error);
            } finally {
                setLoading(false);
            }
        };

        const fetchEntities = async () => {
            try {
                const response = await axiosInstance.get(
                    '/internal-api/v1/client-web/entity-panel/config/list'
                );
                if (response.data && response.data.data) {
                    setEntities(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching entities:", error);
            }
        };

        const fetchBranches = async () => {
            try {
                const response = await axiosInstance.get(
                    '/internal-api/v1/client-web/entity-panel/entity-branch/config/list'
                );
                if (response.data && response.data.data) {
                    setBranches(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching branches:", error);
            }
        };

        const fetchDestinationDetails = async () => {
            if (destinationUuid) {
                try {
                    const response = await axiosInstance.get(
                        `/internal-api/v1/client-web/destination-panel/config/${destinationUuid}/view`
                    );
                    if (response.data && response.data.result) {
                        const data = response.data.result;

                        // Extract unique category codes directly from the API response
                        const categoryCodesFromSubs = [...new Set(
                            data.subCategories?.map(sc => sc.category?.code).filter(Boolean) || []
                        )];
                        setSelectedCategoryCodes(categoryCodesFromSubs);
                        
                        setFormData({
                            name: data.destination?.name || "",
                            displayName: data.destination?.displayName || "",
                            destinationTypeCode: data.destination?.destinationType?.code || "",
                            ownershipTypeCode: data.destination?.ownershipType?.code || "",
                            shortDescription: data.destination?.shortDescription || "",
                            description: data.destination?.description || "",
                            subCategoryCodes: data.subCategories?.map(sc => sc.subCategory?.code) || [],
                            overview: data.detail?.overview || "",
                            highlight: data.detail?.highlight || "",
                            tagCodes: data.tags?.map(t => t.tag?.code) || [],
                            reviewTagIds: data.reviewTags?.map(rt => rt.reviewTag?.id) || [],
                            ownershipEntityIds: data.ownership?.map(item => item.entity?.id).filter(Boolean) || [],
                            ownershipEntityBranchIds: data.ownership?.map(item => item.entityBranch?.id).filter(Boolean) || []
                        });
                        // Existing records stay editable unless the page is explicitly opened in view mode.
                        setIsReadOnly(isViewMode);
                    }
                } catch (error) {
                    console.error("Error fetching destination details:", error);
                }
            }
        };

        fetchMetaData();
        fetchEntities();
        fetchBranches();
        fetchDestinationDetails();
    }, [destinationUuid, isViewMode]);

    useEffect(() => {
        if (isViewMode) {
            setIsReadOnly(true);
        }
    }, [isViewMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleRichTextChange = (name) => (_, editor) => {
        const value = editor.getData();
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleMultiSelectChange = (name, value, isChecked) => {
        setFormData((prevData) => {
            const currentArray = prevData[name] || [];
            if (isChecked) {
                return {
                    ...prevData,
                    [name]: [...currentArray, value]
                };
            } else {
                return {
                    ...prevData,
                    [name]: currentArray.filter(item => item !== value)
                };
            }
        });
    };

    const handleCategoryChange = (categoryCode, isChecked) => {
        setSelectedCategoryCodes((prev) => {
            if (isChecked) {
                return [...prev, categoryCode];
            } else {
                // When unchecking a category, also remove its sub-categories
                const categoryName = metaData.categories.find(c => c.code === categoryCode)?.name;
                if (categoryName) {
                    const subCatCodesToRemove = metaData.subCategories
                        .filter(sub => sub.categoryName === categoryName)
                        .map(sub => sub.code);
                    setFormData((prevData) => ({
                        ...prevData,
                        subCategoryCodes: prevData.subCategoryCodes.filter(
                            code => !subCatCodesToRemove.includes(code)
                        )
                    }));
                }
                return prev.filter(code => code !== categoryCode);
            }
        });
    };

    // Get filtered sub-categories based on selected categories
    const getFilteredSubCategories = () => {
        if (selectedCategoryCodes.length === 0) {
            return [];
        }
        const selectedCategoryNames = selectedCategoryCodes.map(code => 
            metaData.categories.find(c => c.code === code)?.name
        ).filter(Boolean);
        return metaData.subCategories.filter(sub => 
            selectedCategoryNames.includes(sub.categoryName)
        );
    };

    const handleReviewTagsChange = (tagId, isChecked) => {
        setFormData((prevData) => {
            const currentArray = prevData.reviewTagIds || [];
            if (isChecked) {
                return {
                    ...prevData,
                    reviewTagIds: [...currentArray, tagId]
                };
            } else {
                return {
                    ...prevData,
                    reviewTagIds: currentArray.filter(id => id !== tagId)
                };
            }
        });
    };

    const handleEntityChange = (e) => {
        const entityId = e.target.value ? parseInt(e.target.value) : null;
        setFormData((prevData) => ({
            ...prevData,
            ownershipEntityIds: entityId ? [entityId] : [],
            ownershipEntityBranchIds: [] // Clear branch when entity changes
        }));
    };

    const handleBranchChange = (e) => {
        const branchId = e.target.value ? parseInt(e.target.value) : null;
        setFormData((prevData) => ({
            ...prevData,
            ownershipEntityBranchIds: branchId ? [branchId] : []
        }));
    };

    // Get branches for selected entity
    const getAvailableBranches = () => {
        if (formData.ownershipEntityIds.length === 0) return [];
        return branches.filter(branch => 
            branch.entityId === formData.ownershipEntityIds[0]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = isEditMode && destinationUuid
                ? { uuid: destinationUuid, ...formData }
                : formData;
            const endpoint = isEditMode && destinationUuid
                ? `/internal-api/v1/client-web/destination-panel/registration/${destinationUuid}/edit-destination-detail`
                : '/internal-api/v1/client-web/destination-panel/registration/new-destination-detail';
            const response = await axiosInstance({
                method: isEditMode && destinationUuid ? 'put' : 'post',
                url: endpoint,
                data: payload
            });
            console.log("Response:", response.data);

            if (response.data?.status === true || response.data?.result) {
                setDestinationUuid(response.data?.result?.uuid || destinationUuid);
                console.log("Form submitted successfully:", response.data.result);
                // Now this will properly navigate to the location tab
                setActiveTab("location");
                scrollToTop();
            } else {
                console.error("Error submitting form:", response);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
                <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue" />
                Loading metadata...
            </div>
        );
    }

    return (
        <div>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        {/* Destination Name */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">
                                Destination Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Enter destination name"
                                value={formData.name}
                                onChange={handleChange}
                                className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        {/* Display Name */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">
                                Display Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="displayName"
                                placeholder="Public display name"
                                value={formData.displayName}
                                onChange={handleChange}
                                className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                required
                                disabled={isReadOnly}
                            />
                        </div>

                        {/* Destination Type */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">
                                Destination Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="destinationTypeCode"
                                    value={formData.destinationTypeCode}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                    required
                                    disabled={isReadOnly}
                                >
                                    <option value="">Select destination type</option>
                                    {metaData.destinationTypes.map((type) => (
                                        <option key={type.id} value={type.code}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        </div>

                        {/* Ownership Type */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">
                                Ownership Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="ownershipTypeCode"
                                    value={formData.ownershipTypeCode}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                    required
                                    disabled={isReadOnly}
                                >
                                    <option value="">Select ownership type</option>
                                    {metaData.ownershipTypes.map((type) => (
                                        <option key={type.id} value={type.code}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        </div>

                </div>

                {/* Ownership Entity and Branch in one row */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        {/* Ownership Entity */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">Ownership Entity</label>
                            <div className="relative">
                                <select
                                    name="ownershipEntityIds"
                                    value={formData.ownershipEntityIds[0] || ''}
                                    onChange={handleEntityChange}
                                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                    disabled={isReadOnly}
                                >
                                    <option value="">Select entity</option>
                                    {entities.map((entity) => (
                                        <option key={entity.id} value={entity.id}>
                                            {entity.name} {entity.registration ? `(${entity.registration})` : ''}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        </div>

                        {/* Ownership Entity Branch */}
                        <div className="flex flex-col">
                            <label className="mb-1.5 text-xs font-semibold text-slate-700">Ownership Entity Branch</label>
                            <div className="relative">
                                <select
                                    name="ownershipEntityBranchIds"
                                    value={formData.ownershipEntityBranchIds[0] || ''}
                                    onChange={handleBranchChange}
                                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed"
                                    disabled={isReadOnly || formData.ownershipEntityIds.length === 0}
                                    title={formData.ownershipEntityIds.length === 0 ? "Please select an ownership entity first" : ""}
                                >
                                    <option value="">Select branch</option>
                                    {getAvailableBranches().map((branch) => {
                                        const entity = entities.find(e => e.id === branch.entityId);
                                        return (
                                            <option key={branch.id} value={branch.id}>
                                                {branch.name} - {entity?.name} {branch.branchCode ? `(${branch.branchCode})` : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        </div>
                </div>

                    {/* Short Description */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Short Description</label>
                        {isReadOnly ? (
                            <div
                                className="rich-text-preview"
                                dangerouslySetInnerHTML={{
                                    __html: formData.shortDescription || "<p></p>"
                                }}
                            />
                        ) : (
                            <div className="rich-text-editor">
                                <CKEditor
                                    editor={OverviewEditor}
                                    data={formData.shortDescription || ""}
                                    config={{
                                        placeholder: "Brief description"
                                    }}
                                    onChange={handleRichTextChange("shortDescription")}
                                />
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Description</label>
                        {isReadOnly ? (
                            <div
                                className="rich-text-preview"
                                dangerouslySetInnerHTML={{
                                    __html: formData.description || "<p></p>"
                                }}
                            />
                        ) : (
                            <div className="rich-text-editor">
                                <CKEditor
                                    editor={OverviewEditor}
                                    data={formData.description || ""}
                                    config={{
                                        placeholder: "Detailed information for travelers on why they should visit this destination"
                                    }}
                                    onChange={handleRichTextChange("description")}
                                />
                            </div>
                        )}
                    </div>

                    {/* Overview & Highlight */}
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Overview</label>
                            {isReadOnly ? (
                                <div
                                    className="rich-text-preview"
                                    dangerouslySetInnerHTML={{
                                        __html: formData.overview || "<p></p>"
                                    }}
                                />
                            ) : (
                                <div className="rich-text-editor">
                                    <CKEditor
                                        editor={OverviewEditor}
                                        data={formData.overview || ""}
                                        config={{
                                            placeholder: "Enter overview"
                                        }}
                                        onChange={handleRichTextChange("overview")}
                                    />
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Highlight</label>
                            {isReadOnly ? (
                                <div
                                    className="rich-text-preview"
                                    dangerouslySetInnerHTML={{
                                        __html: formData.highlight || "<p></p>"
                                    }}
                                />
                            ) : (
                                <div className="rich-text-editor">
                                    <CKEditor
                                        editor={OverviewEditor}
                                        data={formData.highlight || ""}
                                        config={{
                                            placeholder: "Highlight information"
                                        }}
                                        onChange={handleRichTextChange("highlight")}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Categories (Multi-select) */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Categories</label>
                        <input
                            type="text"
                            placeholder="Search categories..."
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            className="w-full px-3 py-2 mb-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100"
                            disabled={isReadOnly}
                        />
                        <div className="p-3 space-y-1.5 overflow-y-auto border rounded-lg border-slate-200 max-h-40">
                            {metaData.categories
                                .filter(cat =>
                                    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
                                )
                                .map((cat) => (
                                    <label key={cat.id} className={`flex items-center text-sm text-slate-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCategoryCodes.includes(cat.code)}
                                            onChange={(e) => handleCategoryChange(cat.code, e.target.checked)}
                                            className="mr-2 rounded text-brand-blue focus:ring-brand-blue/30"
                                            disabled={isReadOnly}
                                        />
                                        <span>{cat.name}</span>
                                    </label>
                                ))}
                            {metaData.categories.filter(cat =>
                                cat.name.toLowerCase().includes(categorySearch.toLowerCase())
                            ).length === 0 && (
                                <div className="py-2 text-sm text-center text-slate-400">
                                    No categories found
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sub Categories (Multi-select) */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Sub Categories</label>
                        {selectedCategoryCodes.length === 0 && (
                            <div className="mb-2 text-xs italic text-red-500">
                                Please select at least one category above to view sub-categories
                            </div>
                        )}
                        <input
                            type="text"
                            placeholder="Search sub categories..."
                            value={subCategorySearch}
                            onChange={(e) => setSubCategorySearch(e.target.value)}
                            className="w-full px-3 py-2 mb-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:bg-slate-100"
                            disabled={isReadOnly || selectedCategoryCodes.length === 0}
                        />
                        <div className="p-3 space-y-1.5 overflow-y-auto border rounded-lg border-slate-200 max-h-40">
                            {getFilteredSubCategories()
                                .filter(sub =>
                                    sub.name.toLowerCase().includes(subCategorySearch.toLowerCase()) ||
                                    sub.categoryName.toLowerCase().includes(subCategorySearch.toLowerCase())
                                )
                                .map((sub) => (
                                    <label key={sub.id} className={`flex items-center text-sm text-slate-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={formData.subCategoryCodes.includes(sub.code)}
                                            onChange={(e) => handleMultiSelectChange('subCategoryCodes', sub.code, e.target.checked)}
                                            className="mr-2 rounded text-brand-blue focus:ring-brand-blue/30"
                                            disabled={isReadOnly}
                                        />
                                        <span>{sub.name} ({sub.categoryName})</span>
                                    </label>
                                ))}
                            {getFilteredSubCategories().filter(sub =>
                                sub.name.toLowerCase().includes(subCategorySearch.toLowerCase()) ||
                                sub.categoryName.toLowerCase().includes(subCategorySearch.toLowerCase())
                            ).length === 0 && (
                                <div className="py-2 text-sm text-center text-slate-400">
                                    {selectedCategoryCodes.length === 0
                                        ? 'Please select a category first'
                                        : 'No sub categories found'}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Destination Tags (Multi-select) */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Destination Tags</label>
                        <div className="p-3 space-y-1.5 overflow-y-auto border rounded-lg border-slate-200 max-h-40">
                            {metaData.destinationTags.map((tag) => (
                                <label key={tag.id} className={`flex items-center text-sm text-slate-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        checked={formData.tagCodes.includes(tag.code)}
                                        onChange={(e) => handleMultiSelectChange('tagCodes', tag.code, e.target.checked)}
                                        className="mr-2 rounded text-brand-blue focus:ring-brand-blue/30"
                                        disabled={isReadOnly}
                                    />
                                    <span>{tag.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Review Tags (Multi-select) */}
                    <div>
                        <label className="block mb-1.5 text-xs font-semibold text-slate-700">Review Tags</label>
                        <div className="p-3 space-y-1.5 overflow-y-auto border rounded-lg border-slate-200 max-h-40">
                            {metaData.reviewTags.map((tag) => (
                                <label key={tag.id} className={`flex items-center text-sm text-slate-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        checked={formData.reviewTagIds.includes(tag.id)}
                                        onChange={(e) => handleReviewTagsChange(tag.id, e.target.checked)}
                                        className="mr-2 rounded text-brand-blue focus:ring-brand-blue/30"
                                        disabled={isReadOnly}
                                    />
                                    <span>{tag.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex items-center justify-end gap-2 pt-6 border-t border-slate-200">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-semibold border rounded-lg text-slate-600 border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                        >
                            Cancel
                        </button>
                        {!isReadOnly && (
                            <button
                                type="submit"
                                className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
                            >
                                Next
                            </button>
                        )}
                    </div>
            </form>
        </div>
    );
}
